import React, { useState, useEffect, useRef } from 'react';
import './Orders.css';
import ProductSearchModal from './ProductSearchModal';
import { emitCacheUpdated } from '../../utils/cache';
import { API_ENDPOINTS, getAuthHeaders, BASE } from '../../config/api';
import { safeRemoveNode } from '../../utils/safeDom';
import { extractInvoiceFromPDF } from '../../utils/invoiceExtractor';

/**
 * Composant de statistiques complètes (dépenses mensuelles, par catégorie, alertes de prix)
 */
function StatisticsDashboard({ statistics, onClose, compact = false }) {
  const { monthlySpending, categorySpending, priceAlerts, loading } = statistics;

  if (loading) {
    return (
      <div className={`statistics-dashboard ${compact ? 'compact' : ''}`}>
        <div className="statistics-loading">
          <div className="loading-spinner" />
          <p>Chargement des statistiques...</p>
        </div>
      </div>
    );
  }

  const totalSpent = monthlySpending.reduce((sum, m) => sum + parseFloat(m.paid_total || 0), 0);
  const totalInvoices = monthlySpending.length;
  const totalCategories = categorySpending.length;

  // Mode compact : seulement les KPIs
  if (compact) {
    return (
      <div className="kpis-grid">
        <div className="kpi-card">
          <div className="kpi-icon">💰</div>
          <div className="kpi-content">
            <div className="kpi-label">Total dépensé (12 mois)</div>
            <div className="kpi-value">{totalSpent.toFixed(2)} €</div>
          </div>
        </div>
        <div className="kpi-card">
          <div className="kpi-icon">📄</div>
          <div className="kpi-content">
            <div className="kpi-label">Factures</div>
            <div className="kpi-value">{totalInvoices}</div>
          </div>
        </div>
        <div className="kpi-card">
          <div className="kpi-icon">📦</div>
          <div className="kpi-content">
            <div className="kpi-label">Catégories</div>
            <div className="kpi-value">{totalCategories}</div>
          </div>
        </div>
        {priceAlerts.length > 0 && (
          <div className="kpi-card kpi-alert">
            <div className="kpi-icon">🚨</div>
            <div className="kpi-content">
              <div className="kpi-label">Alertes de prix</div>
              <div className="kpi-value">{priceAlerts.length}</div>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="statistics-dashboard">
      <div className="statistics-header">
        <h2>📊 Statistiques de Dépenses</h2>
        {onClose && (
          <button className="statistics-close-btn" onClick={onClose}>
            ×
          </button>
        )}
      </div>

      {/* Résumé */}
      <div className="statistics-summary">
        <div className="summary-card">
          <h3>Total dépensé (12 mois)</h3>
          <p className="amount">{totalSpent.toFixed(2)} €</p>
        </div>
        <div className="summary-card">
          <h3>Nombre de factures</h3>
          <p className="amount">{totalInvoices}</p>
        </div>
        <div className="summary-card">
          <h3>Catégories actives</h3>
          <p className="amount">{totalCategories}</p>
        </div>
        {priceAlerts.length > 0 && (
          <div className="summary-card alert-card">
            <h3>🚨 Alertes de prix</h3>
            <p className="amount">{priceAlerts.length}</p>
          </div>
        )}
      </div>

      {/* Alertes de prix */}
      {priceAlerts.length > 0 && (
        <div className="price-alerts-section">
          <h3>🚨 Alertes de Hausses de Prix ({priceAlerts.length})</h3>
          <div className="alerts-list">
            {priceAlerts.slice(0, 5).map((alert, index) => (
              <div key={index} className="alert-card">
                <div className="alert-header">
                  <h4>{alert.product_title || 'Produit inconnu'}</h4>
                  <span className="category">{alert.category || 'N/A'}</span>
                </div>
                <div className="alert-body">
                  <div className="price-comparison">
                    <span className="previous-price">
                      {parseFloat(alert.previous_price || 0).toFixed(2)} €
                    </span>
                    <span className="arrow">→</span>
                    <span className="current-price">
                      {parseFloat(alert.current_price || 0).toFixed(2)} €
                    </span>
                  </div>
                  <div className="increase-info">
                    <span className="increase-amount">
                      +{parseFloat(alert.increase_amount || 0).toFixed(2)} €
                    </span>
                    <span className="increase-percent">
                      (+{parseFloat(alert.increase_percent || 0).toFixed(1)}%)
                    </span>
                  </div>
                  <div className="dates">
                    <span>
                      {alert.previous_date ? new Date(alert.previous_date).toLocaleDateString('fr-FR') : 'N/A'}
                    </span>
                    <span>→</span>
                    <span>
                      {alert.current_date ? new Date(alert.current_date).toLocaleDateString('fr-FR') : 'N/A'}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Dépenses par catégorie */}
      {categorySpending.length > 0 && (
        <div className="category-spending-section">
          <h3>Dépenses par Catégorie</h3>
          <div className="category-list">
            {categorySpending.map((cat, index) => {
              const percentage = totalSpent > 0 ? ((parseFloat(cat.total_spent || 0) / totalSpent) * 100).toFixed(1) : 0;
              return (
                <div key={index} className="category-item">
                  <div className="category-header">
                    <span className="category-name">{cat.category || 'Non catégorisé'}</span>
                    <span className="category-amount">{parseFloat(cat.total_spent || 0).toFixed(2)} €</span>
                  </div>
                  <div className="category-bar">
                    <div 
                      className="category-bar-fill" 
                      style={{ width: `${percentage}%` }}
                    />
                  </div>
                  <div className="category-details">
                    <span>{percentage}%</span>
                    <span>{cat.item_count || 0} articles</span>
                    <span>{cat.invoice_count || 0} factures</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {monthlySpending.length === 0 && categorySpending.length === 0 && priceAlerts.length === 0 && (
        <div className="statistics-empty">
          <p>Aucune statistique disponible pour le moment.</p>
          <p>Ajoutez des factures pour voir vos statistiques de dépenses.</p>
        </div>
      )}
    </div>
  );
}

/**
 * Composant graphique de budget
 */
function BudgetChart({ data, isLoading }) {
  const [hoveredIndex, setHoveredIndex] = React.useState(null);

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <div className="loading-spinner" />
      </div>
    );
  }

  if (!data || data.length === 0) {
    return null;
  }

  // Graphique stylé avec barres modernes
  const maxValue = Math.max(...data.map(d => d.total));
  const chartHeight = 350;
  const chartWidth = 1000;
  const padding = { top: 40, right: 60, bottom: 80, left: 80 };
  const barSpacing = 20;
  const availableWidth = chartWidth - padding.left - padding.right;
  const barWidth = Math.max(50, (availableWidth / data.length) - barSpacing);
  const totalSpacing = barWidth + barSpacing;
  const graphAreaHeight = chartHeight - padding.top - padding.bottom;

  // Calcul du total pour la légende
  const totalSpent = data.reduce((sum, d) => sum + d.total, 0);
  const totalOrders = data.reduce((sum, d) => sum + d.count, 0);

  return (
    <div className="modern-chart-wrapper">
      <svg 
        width="100%" 
        height={chartHeight} 
        viewBox={`0 0 ${chartWidth} ${chartHeight}`}
        className="modern-chart"
      >
        {/* Définition des gradients */}
        <defs>
          <linearGradient id="barGradient" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#5FA8FF" stopOpacity="1" />
            <stop offset="100%" stopColor="#3B82F6" stopOpacity="0.8" />
          </linearGradient>
          <linearGradient id="barGradientHover" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#7BB3FF" stopOpacity="1" />
            <stop offset="100%" stopColor="#5FA8FF" stopOpacity="0.9" />
          </linearGradient>
          <filter id="glow">
            <feGaussianBlur stdDeviation="3" result="coloredBlur"/>
            <feMerge>
              <feMergeNode in="coloredBlur"/>
              <feMergeNode in="SourceGraphic"/>
            </feMerge>
          </filter>
          <filter id="shadow">
            <feDropShadow dx="0" dy="4" stdDeviation="8" floodOpacity="0.3"/>
          </filter>
        </defs>

        {/* Grille de fond */}
        {[0, 0.25, 0.5, 0.75, 1].map((ratio, i) => {
          const y = padding.top + (graphAreaHeight * (1 - ratio));
          return (
            <g key={`grid-${i}`}>
              <line 
                x1={padding.left} 
                y1={y} 
                x2={chartWidth - padding.right} 
                y2={y} 
                stroke="rgba(255, 255, 255, 0.05)" 
                strokeWidth="1"
                strokeDasharray="4,4"
              />
            </g>
          );
        })}

        {/* Axe Y avec style */}
        <line 
          x1={padding.left} 
          y1={padding.top} 
          x2={padding.left} 
          y2={chartHeight - padding.bottom} 
          stroke="rgba(255, 255, 255, 0.2)" 
          strokeWidth="2" 
        />
        
        {/* Graduations Y stylées */}
        {[0, 0.25, 0.5, 0.75, 1].map((ratio, i) => {
          const value = Math.round(maxValue * ratio);
          const y = padding.top + (graphAreaHeight * (1 - ratio));
          return (
            <g key={`y-tick-${i}`}>
              <line 
                x1={padding.left - 5} 
                y1={y} 
                x2={padding.left} 
                y2={y} 
                stroke="rgba(255, 255, 255, 0.3)" 
                strokeWidth="2" 
              />
              <text 
                x={padding.left - 15} 
                y={y + 4} 
                textAnchor="end" 
                fill="var(--color-light-gray)" 
                fontSize="13"
                fontWeight="500"
              >
                {value}€
              </text>
            </g>
          );
        })}

        {/* Barres avec style moderne */}
        {data.map((item, i) => {
          const barHeight = (item.total / maxValue) * graphAreaHeight;
          const x = padding.left + (i * totalSpacing) + (barSpacing / 2);
          const y = padding.top + graphAreaHeight - barHeight;
          const isHovered = hoveredIndex === i;
          
          return (
            <g 
              key={`bar-${i}`}
              onMouseEnter={() => setHoveredIndex(i)}
              onMouseLeave={() => setHoveredIndex(null)}
              style={{ cursor: 'pointer' }}
            >
              {/* Ombre de la barre */}
              <rect
                x={x + 2}
                y={y + 2}
                width={barWidth}
                height={barHeight}
                rx="8"
                fill="rgba(0, 0, 0, 0.3)"
                opacity="0.5"
              />
              {/* Barre principale avec gradient */}
              <rect
                x={x}
                y={y}
                width={barWidth}
                height={barHeight}
                rx="8"
                fill={isHovered ? "url(#barGradientHover)" : "url(#barGradient)"}
                filter={isHovered ? "url(#glow)" : "url(#shadow)"}
                style={{ transition: 'all 0.3s ease' }}
              />
              
              {/* Ligne de valeur au-dessus */}
              {isHovered && (
                <g>
                  <rect
                    x={x + barWidth / 2 - 35}
                    y={y - 35}
                    width="70"
                    height="28"
                    rx="6"
                    fill="rgba(20, 20, 20, 0.95)"
                    stroke="var(--color-blue)"
                    strokeWidth="1"
                  />
                  <text
                    x={x + barWidth / 2}
                    y={y - 18}
                    textAnchor="middle"
                    fill="var(--color-white)"
                    fontSize="13"
                    fontWeight="700"
                  >
                    {item.total.toFixed(2)}€
                  </text>
                </g>
              )}
              
              {/* Valeur au-dessus de la barre (toujours visible) */}
              <text
                x={x + barWidth / 2}
                y={y - 8}
                textAnchor="middle"
                fill="var(--color-white)"
                fontSize="12"
                fontWeight="600"
                opacity={isHovered ? 0 : 0.9}
                style={{ transition: 'opacity 0.2s ease' }}
              >
                {item.total.toFixed(0)}€
              </text>
              
              {/* Label du mois stylé */}
              <text
                x={x + barWidth / 2}
                y={chartHeight - padding.bottom + 25}
                textAnchor="middle"
                fill="var(--color-light-gray)"
                fontSize="12"
                fontWeight="500"
              >
                {item.label}
              </text>
            </g>
          );
        })}

        {/* Ligne de tendance (optionnelle - peut être ajoutée plus tard) */}
      </svg>

      {/* Légende moderne en bas */}
      <div className="chart-legend">
        <div className="legend-item">
          <div className="legend-color" style={{ background: 'linear-gradient(180deg, #5FA8FF 0%, #3B82F6 100%)' }}></div>
          <span className="legend-label">Total dépensé</span>
          <span className="legend-value">{totalSpent.toFixed(2)}€</span>
        </div>
        <div className="legend-item">
          <div className="legend-icon">📦</div>
          <span className="legend-label">Commandes</span>
          <span className="legend-value">{totalOrders}</span>
        </div>
        <div className="legend-item">
          <div className="legend-icon">📅</div>
          <span className="legend-label">Période</span>
          <span className="legend-value">{data.length} mois</span>
        </div>
      </div>
    </div>
  );
}

/**
 * Onglet Commandes - Gestion des commandes et suivi du budget
 * Features:
 * - Graphique de suivi du budget
 * - Upload de factures par drag & drop
 * - Liste des commandes récentes
 * - Moteur de recherche de produits
 */
function Orders({ onNavigate }) {
  const [isLoading, setIsLoading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(null);
  const [orders, setOrders] = useState([]);
  const [budgetData, setBudgetData] = useState([]);
  const [isSearchModalOpen, setIsSearchModalOpen] = useState(false);
  const [statistics, setStatistics] = useState({
    monthlySpending: [],
    categorySpending: [],
    priceAlerts: [],
    loading: true
  });
  const [purchasedItems, setPurchasedItems] = useState([]);
  const [purchasedItemsLoading, setPurchasedItemsLoading] = useState(false);
  const [purchasedItemsPagination, setPurchasedItemsPagination] = useState({
    total: 0,
    limit: 20,
    offset: 0,
    has_more: false
  });
  const [purchasedItemsFilters, setPurchasedItemsFilters] = useState({
    category: '',
    distributor: '',
    from: '',
    to: ''
  });
  const fileInputRef = useRef(null);
  const dropZoneRef = useRef(null);

  // Récupération des commandes au montage
  useEffect(() => {
    fetchOrders();
    fetchBudgetData();
    fetchAllStatistics();
    fetchPurchasedItems();
  }, []);

  // Recharger les articles achetés quand les filtres ou la pagination changent
  useEffect(() => {
    fetchPurchasedItems();
  }, [purchasedItemsPagination.offset, purchasedItemsFilters]);

  /**
   * Récupère les commandes de l'utilisateur
   */
  const fetchOrders = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(API_ENDPOINTS.COMMANDES_LIST({ limit: 100, offset: 0 }), {
        headers: getAuthHeaders()
      });

      if (response.ok) {
        const data = await response.json();
        setOrders(data.items || []);
      } else {
        console.error('Failed to fetch orders:', response.status);
      }
    } catch (error) {
      console.error('Error fetching orders:', error);
      showToast('Erreur lors de la récupération des commandes', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Récupère les données de budget pour le graphique
   */
  const fetchBudgetData = async () => {
    try {
      // Essayer d'abord l'endpoint de statistiques mensuelles
      const statsResponse = await fetch(API_ENDPOINTS.COMMANDES_STATS_MONTHLY_SPEND({ months: 12 }), {
        headers: getAuthHeaders()
      });

      if (statsResponse.ok) {
        const statsData = await statsResponse.json();
        // Convertir les données du backend au format attendu par BudgetChart
        const formatted = Array.isArray(statsData) ? statsData.map(item => ({
          month: item.month_start,
          total: parseFloat(item.paid_total || 0),
          count: 1,
          label: new Date(item.month_start).toLocaleDateString('fr-FR', { month: 'short', year: 'numeric' })
        })) : [];
        setBudgetData(formatted);
      } else {
        // Fallback sur l'ancienne méthode si l'endpoint n'existe pas
        const response = await fetch(API_ENDPOINTS.COMMANDES_LIST({ limit: 1000, offset: 0 }), {
          headers: getAuthHeaders()
        });

        if (response.ok) {
          const data = await response.json();
          const aggregated = aggregateBudgetData(data.items || []);
          setBudgetData(aggregated);
        }
      }
    } catch (error) {
      console.error('Error fetching budget data:', error);
      // Fallback silencieux
      try {
        const response = await fetch(API_ENDPOINTS.COMMANDES_LIST({ limit: 1000, offset: 0 }), {
          headers: getAuthHeaders()
        });
        if (response.ok) {
          const data = await response.json();
          const aggregated = aggregateBudgetData(data.items || []);
          setBudgetData(aggregated);
        }
      } catch (fallbackError) {
        console.error('Error fetching budget data (fallback):', fallbackError);
      }
    }
  };

  /**
   * Récupère toutes les statistiques (dépenses mensuelles, par catégorie, alertes de prix)
   */
  const fetchAllStatistics = async () => {
    setStatistics(prev => ({ ...prev, loading: true }));
    try {
      const [monthlyResponse, categoryResponse, alertsResponse] = await Promise.all([
        fetch(API_ENDPOINTS.COMMANDES_STATS_MONTHLY_SPEND({ months: 12 }), {
          headers: getAuthHeaders()
        }),
        fetch(API_ENDPOINTS.COMMANDES_STATS_SPEND_BY_CATEGORY({ months: 12, status: 'paid' }), {
          headers: getAuthHeaders()
        }),
        fetch(API_ENDPOINTS.COMMANDES_STATS_PRICE_ALERTS({ min_increase_percent: 15, min_increase_amount: 5, months: 6 }), {
          headers: getAuthHeaders()
        })
      ]);

      const monthlyData = monthlyResponse.ok ? await monthlyResponse.json() : [];
      const categoryData = categoryResponse.ok ? await categoryResponse.json() : [];
      const alertsData = alertsResponse.ok ? await alertsResponse.json() : { items: [], count: 0 };

      setStatistics({
        monthlySpending: Array.isArray(monthlyData) ? monthlyData : [],
        categorySpending: Array.isArray(categoryData) ? categoryData : [],
        priceAlerts: alertsData.items || [],
        loading: false
      });
    } catch (error) {
      console.error('Error fetching statistics:', error);
      setStatistics(prev => ({ ...prev, loading: false }));
    }
  };

  /**
   * Récupère les articles achetés avec filtres et pagination
   */
  const fetchPurchasedItems = async () => {
    setPurchasedItemsLoading(true);
    try {
      const params = {
        limit: purchasedItemsPagination.limit,
        offset: purchasedItemsPagination.offset,
        ...(purchasedItemsFilters.category && { category: purchasedItemsFilters.category }),
        ...(purchasedItemsFilters.distributor && { distributor: purchasedItemsFilters.distributor }),
        ...(purchasedItemsFilters.from && { from: purchasedItemsFilters.from }),
        ...(purchasedItemsFilters.to && { to: purchasedItemsFilters.to })
      };

      const response = await fetch(API_ENDPOINTS.COMMANDES_PURCHASED_ITEMS(params), {
        headers: getAuthHeaders()
      });

      if (response.ok) {
        const data = await response.json();
        setPurchasedItems(data.items || []);
        setPurchasedItemsPagination({
          total: data.total || 0,
          limit: data.limit || 20,
          offset: data.offset || 0,
          has_more: data.has_more || false
        });
      } else {
        console.error('Error fetching purchased items:', response.status);
      }
    } catch (error) {
      console.error('Error fetching purchased items:', error);
    } finally {
      setPurchasedItemsLoading(false);
    }
  };

  /**
   * Agrège les commandes par mois pour le graphique
   */
  const aggregateBudgetData = (items) => {
    if (!items || items.length === 0) return [];

    const monthsMap = new Map();
    
    items.forEach(item => {
      const date = new Date(item.recorded_at || item.purchased_at);
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      
      if (!monthsMap.has(monthKey)) {
        monthsMap.set(monthKey, {
          month: monthKey,
          total: 0,
          count: 0
        });
      }
      
      const monthData = monthsMap.get(monthKey);
      monthData.total += parseFloat(item.unit_price || 0) * (item.quantity || 1);
      monthData.count += 1;
    });

    // Trier et formater les données
    return Array.from(monthsMap.values())
      .sort((a, b) => a.month.localeCompare(b.month))
      .map(item => ({
        month: item.month,
        label: getMonthLabel(item.month),
        total: Math.round(item.total * 100) / 100,
        count: item.count
      }));
  };

  /**
   * Convertit une clé de mois (YYYY-MM) en libellé lisible
   */
  const getMonthLabel = (monthKey) => {
    const [year, month] = monthKey.split('-');
    const date = new Date(parseInt(year), parseInt(month) - 1);
    return date.toLocaleDateString('fr-FR', { month: 'short', year: 'numeric' });
  };

  /**
   * Gère le drag & drop
   */
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    // Vérifier si on quitte vraiment la zone (pas juste un enfant)
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setIsDragging(false);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (files.length > 0) {
      await handleFileUpload(files[0]);
    }
  };

  /**
   * Gère le clic sur le bouton ou la zone de drop
   */
  const handleZoneClick = () => {
    fileInputRef.current?.click();
  };

  /**
   * Gère le changement de fichier (support multi-fichiers)
   */
  const handleFileChange = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length > 0) {
      await handleMultipleFilesUpload(files);
    }
  };

  /**
   * Traite l'upload de plusieurs fichiers (factures)
   */
  const handleMultipleFilesUpload = async (files) => {
    const pdfFiles = files.filter(f => f.type === 'application/pdf');
    
    if (pdfFiles.length === 0) {
      showToast('Veuillez uploader des fichiers PDF', 'error');
      return;
    }

    setIsLoading(true);
    let successCount = 0;
    let errorCount = 0;

    for (let i = 0; i < pdfFiles.length; i++) {
      const file = pdfFiles[i];
      const progress = ((i + 1) / pdfFiles.length) * 100;
      setUploadProgress(progress);

      try {
        await handleFileUpload(file);
        successCount++;
      } catch (error) {
        console.error(`❌ Erreur sur ${file.name}:`, error);
        errorCount++;
      }
    }

    setIsLoading(false);
    setUploadProgress(null);

    // Afficher résultat final
    if (successCount > 0) {
      showSuccessModal(successCount, pdfFiles.length);
    }
    
    if (errorCount > 0) {
      showToast(`${errorCount} facture(s) en erreur`, 'error');
    }

    // Réinitialiser l'input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }

    // Recharger les données
    await fetchOrders();
    await fetchBudgetData();
    // Recharger les statistiques complètes
    await fetchAllStatistics();
    // Recharger les articles achetés
    await fetchPurchasedItems();
  };

  /**
   * Traite l'upload d'un seul fichier (facture)
   */
  const handleFileUpload = async (file) => {
    if (!file) throw new Error('Fichier manquant');

    // Vérifier que c'est un PDF
    if (file.type !== 'application/pdf') {
      throw new Error('Format invalide (PDF requis)');
    }

    console.log('📄 Début de l\'extraction du PDF:', file.name);
    
    // Extraire les données de la facture
    const extractedData = await extractInvoiceFromPDF(file, { debug: true });
    
    // Logs détaillés dans la console
    console.log('INVOICE_EXTRACTED', extractedData);
    console.table(extractedData.items);
    console.log('═══════════════════════════════════════════════════');
    console.log('✅ EXTRACTION RÉUSSIE - Données extraites de la facture');
    console.log('═══════════════════════════════════════════════════');
    console.log('📋 Informations générales:');
    console.log('   Fournisseur:', extractedData.supplier);
    console.log('   Numéro client:', extractedData.client_number);
    console.log('   Numéro de facture:', extractedData.invoice_number);
    console.log('   Date de la facture:', extractedData.invoice_date);
    console.log('   Montant total TTC:', extractedData.total_ttc, '€');
    console.log('🛒 Articles extraits:', extractedData.items.length);
    console.log('───────────────────────────────────────────────────');
    
    extractedData.items.forEach((item, index) => {
      console.log(`Article ${index + 1}:`);
      console.log('   Référence:', item.reference);
      console.log('   Nom:', item.name);
      console.log('   Quantité:', item.quantity);
      console.log('   Prix unitaire TTC:', item.unit_price_ttc, '€');
      console.log('   Remise:', item.discount_percent, '%');
    });
    
    console.log('═══════════════════════════════════════════════════');
    
    // Envoyer au backend Stericab
    await sendInvoiceToBackend(extractedData);
  };

  /**
   * Envoie la facture au backend Stericab
   * Gère les factures simples (GACD) et multiples (Megadental)
   */
  const sendInvoiceToBackend = async (extractedData) => {
    const isMultipleInvoices = extractedData.invoices && Array.isArray(extractedData.invoices);
    
    if (isMultipleInvoices) {
      // Cas Megadental : plusieurs factures dans un seul PDF
      console.log(`📦 Traitement de ${extractedData.invoices.length} factures Megadental...`);
      
      for (const invoice of extractedData.invoices) {
        try {
          const result = await uploadSingleInvoice(invoice);
          const originalItems = result._originalItems || invoice.items || [];
          logInvoiceResult(invoice.invoice_number, result, originalItems);
        } catch (error) {
          console.error(`❌ Erreur facture ${invoice.invoice_number}:`, error);
          throw error;
        }
      }
      
    } else {
      // Cas simple : une seule facture (GACD)
      const result = await uploadSingleInvoice(extractedData);
      const originalItems = result._originalItems || extractedData.items || [];
      logInvoiceResult(extractedData.invoice_number, result, originalItems);
    }
  };

  /**
   * Upload une facture unique vers l'API backend
   */
  const uploadSingleInvoice = async (invoiceData) => {
    // Le code distributeur doit être en MAJUSCULES pour la clé étrangère invoices_distributor_fkey
    // Le backend normalisera ensuite en minuscules pour product_references.source
    const distributor = invoiceData.supplier.toUpperCase();
    
    // Calculer le statut de paiement automatique
    const paymentStatus = calculatePaymentStatus(invoiceData.invoice_date);
    
    // Format API Stericab v2 (voir API_COMPLETE_GUIDE.md)
    // payment_status accepte: "pending", "paid", "overdue"
    const payload = {
      distributor: distributor,
      invoice_number: invoiceData.invoice_number,
      invoice_date: invoiceData.invoice_date,
      total_ttc: invoiceData.total_ttc,
      currency: 'EUR',
      payment_status: paymentStatus, // ✅ Maintenant supporté par le backend
      items: invoiceData.items.map(item => ({
        ref: item.reference,
        name: item.name,
        quantity: item.quantity,
        unit_price: item.unit_price_ttc
      }))
    };

    console.log(`🚀 Envoi de la facture au backend (statut: ${paymentStatus}):`, payload);

    const response = await fetch(API_ENDPOINTS.COMMANDES_INVOICE_CREATE(), {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(payload)
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || `Erreur ${response.status}`);
    }

    const result = await response.json();
    console.log('✅ Réponse du backend:', result);
    
    // Retourner le résultat avec les items originaux pour le logging
    return { ...result, _originalItems: invoiceData.items };
  };

  /**
   * Calcule le statut de paiement en fonction de l'âge de la facture
   * Si > 2 mois → 'paid'
   * Sinon → 'pending'
   */
  const calculatePaymentStatus = (invoiceDate) => {
    if (!invoiceDate) return 'pending';
    
    const today = new Date();
    const invoice = new Date(invoiceDate);
    
    // Calculer la différence en mois
    const monthsDiff = (today.getFullYear() - invoice.getFullYear()) * 12 
                     + (today.getMonth() - invoice.getMonth());
    
    // Si la facture a plus de 2 mois, elle est considérée comme payée
    const status = monthsDiff > 2 ? 'paid' : 'pending';
    
    console.log(`📅 Facture du ${invoiceDate}: ${monthsDiff} mois → statut: ${status}`);
    
    return status;
  };

  /**
   * Log le résultat d'upload d'une facture dans la console
   */
  const logInvoiceResult = (invoiceNumber, result, originalItems = []) => {
    const { items_inserted, unknown_refs, unknown_details } = result;
    
    // Extraire toutes les références envoyées
    const allReferences = originalItems.map(item => item.reference || item.ref).filter(Boolean);
    const totalReferences = allReferences.length;
    
    console.log('═══════════════════════════════════════════════════');
    console.log(`📊 RÉSULTAT UPLOAD - Facture ${invoiceNumber}`);
    console.log('═══════════════════════════════════════════════════');
    console.log(`📦 Total articles dans la facture: ${totalReferences}`);
    console.log(`✅ Articles insérés: ${items_inserted}`);
    
    // Vérifier si toutes les références ont été trouvées
    if (unknown_refs === 0 && totalReferences > 0) {
      console.log('✅ ✅ ✅ TOUTES LES RÉFÉRENCES ONT ÉTÉ TROUVÉES ! ✅ ✅ ✅');
      console.log(`📋 Références trouvées (${totalReferences}):`);
      allReferences.forEach((ref, index) => {
        console.log(`   ${index + 1}. ${ref} ✅`);
      });
    } else if (unknown_refs > 0) {
      // Séparer les références trouvées et non trouvées
      const foundReferences = allReferences.filter(ref => 
        !unknown_details || !unknown_details.includes(ref)
      );
      const notFoundReferences = unknown_details || [];
      
      console.warn(`⚠️ ${unknown_refs} référence(s) non trouvée(s) sur ${totalReferences}`);
      
      if (foundReferences.length > 0) {
        console.log(`✅ Références trouvées (${foundReferences.length}):`);
        foundReferences.forEach((ref, index) => {
          console.log(`   ${index + 1}. ${ref} ✅`);
        });
      }
      
      if (notFoundReferences.length > 0) {
        console.warn(`❌ Références NON trouvées (${notFoundReferences.length}):`);
        notFoundReferences.forEach((ref, index) => {
          console.warn(`   ${index + 1}. ${ref} ❌`);
        });
      }
    } else {
      console.log('ℹ️ Aucune référence à vérifier');
    }
    
    console.log('═══════════════════════════════════════════════════');
  };

  /**
   * Affiche un modal de succès après upload
   */
  const showSuccessModal = (successCount, totalCount) => {
    const message = successCount === totalCount
      ? `✅ ${successCount} facture(s) enregistrée(s) avec succès !`
      : `✅ ${successCount}/${totalCount} facture(s) enregistrée(s)`;
    
    showToast(message, 'success');
  };

  /**
   * Affiche une notification toast
   */
  const showToast = (message, type = 'success') => {
    const toast = document.createElement('div');
    toast.className = `toast toast-${type}`;
    
    const icon = type === 'success' ? '✓' : type === 'warning' ? '⚠' : '✗';
    
    toast.innerHTML = `
      <span class="toast-icon">${icon}</span>
      <span class="toast-message">${message}</span>
    `;

    document.body.appendChild(toast);

    // Supprimer après 4 secondes (plus long pour warnings)
    const duration = type === 'warning' ? 5000 : 3000;
    setTimeout(() => {
      toast.style.animation = 'slideIn 0.3s ease reverse';
      setTimeout(() => {
        safeRemoveNode(toast);
      }, 300);
    }, duration);
  };

  /**
   * Gère l'ajout de produits depuis le modal de recherche
   */
  const handleProductsAdded = async (cartItems) => {
    try {
      // Convertir les items du panier en format API
      const items = cartItems.map(item => ({
        product_id: item.id,
        unit_price: item.price,
        quantity: item.quantity,
        note: `Commande depuis le catalogue`
      }));

      const response = await fetch(API_ENDPOINTS.COMMANDES_CREATE(), {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          items: items,
          source: 'CATALOGUE',
          currency: 'EUR',
          meta: {}
        })
      });

      if (response.ok) {
        showToast('Commande créée avec succès', 'success');
        await fetchOrders();
        await fetchBudgetData();
        emitCacheUpdated('orders');
      } else {
        const error = await response.json();
        throw new Error(error.message || 'Failed to create order');
      }
    } catch (error) {
      console.error('Error creating order:', error);
      showToast(error.message || 'Erreur lors de la création de la commande', 'error');
    }
  };

  /**
   * Navigation vers une autre page
   */
  const handleNavigate = (page) => {
    if (onNavigate) {
      onNavigate(page);
    }
  };

  // Navigation items (même structure que les autres pages)
  const navItems = [
    { key: 'tasks', icon: '✓', label: 'Tâches' },
    { key: 'sterilization', icon: '🔥', label: 'Stérilisation' },
    { key: 'operators', icon: '👥', label: 'Opérateurs' },
    { key: 'activity', icon: '📊', label: 'Activité' },
    { key: 'agenda', icon: '📅', label: 'Agenda' },
    { key: 'orders', icon: '🛒', label: 'Commandes', active: true },
  ];

  return (
    <div className="orders-container">
      {/* Sidebar */}
      <div className="sidebar">
        <div className="logo">CabConnect</div>
        <nav className="nav-menu">
          {navItems.map(item => (
            <div
              key={item.key}
              className={`nav-item ${item.active ? 'active' : ''}`}
              onClick={() => handleNavigate(item.key)}
            >
              <span className="nav-icon">{item.icon}</span>
              <span>{item.label}</span>
            </div>
          ))}
        </nav>
      </div>

      {/* Contenu principal */}
      <div className="orders-content">
        {/* Header */}
        <div className="orders-header">
          <div className="header-left">
            <h1>Commandes</h1>
          </div>
          <div className="header-right-buttons">
            <button className="new-order-btn" onClick={() => setIsSearchModalOpen(true)}>
              <span>+</span>
              Nouvelle commande
            </button>
            <button className="upload-invoice-btn" onClick={() => fileInputRef.current?.click()}>
              📄 Upload facture
            </button>
          </div>
        </div>

        {/* KPIs en haut - Toujours visibles */}
        <div className="kpis-section">
          <StatisticsDashboard statistics={statistics} compact={true} />
        </div>

        {/* Graphique de budget - Stylé et moderne */}
        <div className="budget-chart-card">
          <div className="chart-header">
            <h2>📊 Suivi du budget</h2>
            <div className="chart-subtitle">Évolution des dépenses mensuelles</div>
          </div>
          
          <div className="chart-container">
            {/* Zone de drop overlay - toujours présente mais invisible sauf au hover/drag */}
            <div 
              ref={dropZoneRef}
              className={`drop-zone-overlay ${isDragging ? 'active' : ''}`}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              {isDragging && (
                <div className="drop-zone-message">
                  <div className="drop-icon">📄</div>
                  <p>Déposez votre facture ici</p>
                </div>
              )}
            </div>

            {isLoading ? (
              <div className="chart-loading">
                <div className="loading-spinner" />
                <p>Chargement des données...</p>
              </div>
            ) : budgetData.length === 0 ? (
              <div className="chart-empty">
                <div className="chart-empty-icon">📈</div>
                <p>Aucune donnée disponible</p>
                <p className="chart-empty-hint">Téléchargez une facture pour commencer</p>
              </div>
            ) : (
              <BudgetChart data={budgetData} isLoading={isLoading} />
            )}
          </div>
        </div>

        {/* Liste des articles achetés */}
        <div className="purchased-items-section">
          <div className="section-header">
            <h2>📦 Articles Achetés</h2>
            <div className="section-actions">
              <button 
                className="refresh-btn"
                onClick={fetchPurchasedItems}
                disabled={purchasedItemsLoading}
              >
                🔄 Actualiser
              </button>
            </div>
          </div>

          {/* Filtres */}
          <div className="purchased-items-filters">
            <input
              type="text"
              placeholder="Filtrer par catégorie..."
              value={purchasedItemsFilters.category}
              onChange={(e) => {
                setPurchasedItemsFilters({ ...purchasedItemsFilters, category: e.target.value });
                setPurchasedItemsPagination({ ...purchasedItemsPagination, offset: 0 });
              }}
              className="filter-input"
            />
            <input
              type="text"
              placeholder="Filtrer par distributeur..."
              value={purchasedItemsFilters.distributor}
              onChange={(e) => {
                setPurchasedItemsFilters({ ...purchasedItemsFilters, distributor: e.target.value });
                setPurchasedItemsPagination({ ...purchasedItemsPagination, offset: 0 });
              }}
              className="filter-input"
            />
            <input
              type="date"
              placeholder="Date début"
              value={purchasedItemsFilters.from}
              onChange={(e) => {
                setPurchasedItemsFilters({ ...purchasedItemsFilters, from: e.target.value });
                setPurchasedItemsPagination({ ...purchasedItemsPagination, offset: 0 });
              }}
              className="filter-input"
            />
            <input
              type="date"
              placeholder="Date fin"
              value={purchasedItemsFilters.to}
              onChange={(e) => {
                setPurchasedItemsFilters({ ...purchasedItemsFilters, to: e.target.value });
                setPurchasedItemsPagination({ ...purchasedItemsPagination, offset: 0 });
              }}
              className="filter-input"
            />
            {(purchasedItemsFilters.category || purchasedItemsFilters.distributor || purchasedItemsFilters.from || purchasedItemsFilters.to) && (
              <button
                className="clear-filters-btn"
                onClick={() => {
                  setPurchasedItemsFilters({ category: '', distributor: '', from: '', to: '' });
                  setPurchasedItemsPagination({ ...purchasedItemsPagination, offset: 0 });
                }}
              >
                ✕ Effacer filtres
              </button>
            )}
          </div>

          {/* Tableau des articles */}
          {purchasedItemsLoading ? (
            <div className="loading-container">
              <div className="loading-spinner" />
              <p>Chargement des articles...</p>
            </div>
          ) : purchasedItems.length === 0 ? (
            <div className="empty-state">
              <p>Aucun article trouvé</p>
            </div>
          ) : (
            <>
              <div className="purchased-items-table-container">
                <table className="purchased-items-table">
                  <thead>
                    <tr>
                      <th>Produit</th>
                      <th>Catégorie</th>
                      <th>Référence</th>
                      <th>Prix réf.</th>
                      <th>Prix payé</th>
                      <th>Remise</th>
                      <th>Quantité</th>
                      <th>Total</th>
                      <th>Distributeur</th>
                      <th>Facture</th>
                      <th>Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {purchasedItems.map((item) => {
                      const discount = item.discount_percent !== null 
                        ? item.discount_percent 
                        : (item.reference_price && item.unit_price 
                          ? ((item.reference_price - item.unit_price) / item.reference_price * 100)
                          : null);
                      const total = item.unit_price && item.quantity 
                        ? (item.unit_price * item.quantity)
                        : null;

                      return (
                        <tr key={item.id}>
                          <td className="product-title">{item.product_title || 'Produit non identifié'}</td>
                          <td className="category">{item.category || 'Non classifié'}</td>
                          <td className="reference">{item.product_reference || '-'}</td>
                          <td className="price">
                            {item.reference_price ? `${item.reference_price.toFixed(2)} €` : '-'}
                          </td>
                          <td className="price price-paid">
                            {item.unit_price ? `${item.unit_price.toFixed(2)} €` : '-'}
                          </td>
                          <td className="discount">
                            {discount !== null ? (
                              <span className={discount > 0 ? 'discount-positive' : 'discount-negative'}>
                                {discount > 0 ? '-' : '+'}{Math.abs(discount).toFixed(1)}%
                              </span>
                            ) : '-'}
                          </td>
                          <td className="quantity">{item.quantity || 0}</td>
                          <td className="total">
                            {total !== null ? `${total.toFixed(2)} €` : '-'}
                          </td>
                          <td className="distributor">{item.distributor || '-'}</td>
                          <td className="invoice-number">{item.invoice_number || '-'}</td>
                          <td className="date">
                            {item.invoice_date 
                              ? new Date(item.invoice_date).toLocaleDateString('fr-FR')
                              : '-'
                            }
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {purchasedItemsPagination.total > 0 && (
                <div className="pagination">
                  <button
                    className="pagination-btn"
                    disabled={purchasedItemsPagination.offset === 0 || purchasedItemsLoading}
                    onClick={() => {
                      setPurchasedItemsPagination({
                        ...purchasedItemsPagination,
                        offset: Math.max(0, purchasedItemsPagination.offset - purchasedItemsPagination.limit)
                      });
                    }}
                  >
                    ← Précédent
                  </button>
                  <span className="pagination-info">
                    {purchasedItemsPagination.offset + 1} - {Math.min(purchasedItemsPagination.offset + purchasedItemsPagination.limit, purchasedItemsPagination.total)} sur {purchasedItemsPagination.total}
                  </span>
                  <button
                    className="pagination-btn"
                    disabled={!purchasedItemsPagination.has_more || purchasedItemsLoading}
                    onClick={() => {
                      setPurchasedItemsPagination({
                        ...purchasedItemsPagination,
                        offset: purchasedItemsPagination.offset + purchasedItemsPagination.limit
                      });
                    }}
                  >
                    Suivant →
                  </button>
                </div>
              )}
            </>
          )}
        </div>

        {/* Input file caché (support multi-fichiers) */}
        <input
          ref={fileInputRef}
          type="file"
          accept=".pdf,application/pdf"
          multiple
          onChange={handleFileChange}
          className="file-input"
        />

        {/* Dashboard de statistiques détaillé (après upload) */}
        {statistics.monthlySpending.length > 0 && (
          <div className="statistics-dashboard-container">
            <StatisticsDashboard 
              statistics={statistics}
            />
          </div>
        )}

        {/* Modal de recherche */}
        <ProductSearchModal
          isOpen={isSearchModalOpen}
          onClose={() => setIsSearchModalOpen(false)}
          onAddProduct={handleProductsAdded}
        />
      </div>
    </div>
  );
}

export default Orders;

